import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useState} from "react";

import {useOnMountEffect, View} from "@khanacademy/wonder-blocks-core";
import {TextField} from "@khanacademy/wonder-blocks-form";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {Spring, Strut} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelLarge, LabelSmall} from "@khanacademy/wonder-blocks-typography";
import {getCoeditorVariables} from "@khan/guide-coeditor-core";
import {useGuidePreferences, useRunGuideComponent} from "@khan/guide-core";

import {ActionBubbles} from "../action-bubbles.tsx";
import {GuideIcon32} from "../icons/guide-icon-32.tsx";
import {LoadingDots} from "../loading-dots.tsx";

import {type Suggestion} from "./suggestions.ts";

import xIcon from "@phosphor-icons/core/bold/x-bold.svg";
import paperAirplaneIcon from "@phosphor-icons/core/fill/paper-plane-tilt-fill.svg";

interface Props {
    suggestion: Suggestion;
    updateSuggestion: (suggestion: Suggestion | null) => void;
    handleSuggestionClick: (suggestion: Suggestion) => void;
}

/**
 * This component renders the input that can appear within the "Ask Khanmigo"
 * popup when your selected suggestion requires additional information. For
 * example, a user may select some text and click on "Make changes to this".
 * At that point, the dropdown would change to this text field so the user
 * can provide details on the kinds of changes they want.
 */
export default function AskKhanmigoTextInput(props: Props) {
    const {suggestion, handleSuggestionClick, updateSuggestion} = props;

    const [loading, setLoading] = useState(true);
    const [dynamicSuggestions, setDynamicSuggestions] = useState<string[]>([]);

    const {preferences} = useGuidePreferences();
    const getSuggestions = useRunGuideComponent("CoeditingGetSuggestions");

    const inputText = suggestion?.additionalText ?? "";

    useOnMountEffect(() => {
        const {prefix, selection, suffix} = getCoeditorVariables();

        setLoading(true);

        getSuggestions({
            state: {
                prefix: prefix ?? "",
                selection: selection ?? "",
                suffix: suffix ?? "",
            },
            changeInstruction: suggestion.text(),
            guideLanguageCode: preferences.guideLanguageCode,
        }).then((response) => {
            setLoading(false);

            if (response.ok) {
                setDynamicSuggestions(response.output.suggestions);
            }
        });
    });

    return (
        <View style={styles.wrapper}>
            <View style={styles.openerContainer}>
                <GuideIcon32 id="khanmigo-icon" style={styles.khanmigoIcon} />
                <LabelLarge style={styles.editingTitle}>
                    {suggestion.text()}
                </LabelLarge>
                <Spring />
                <View style={styles.caretIcon}>
                    <IconButton
                        size="xsmall"
                        icon={xIcon}
                        kind="tertiary"
                        onClick={() => {
                            updateSuggestion(null);
                        }}
                    />
                </View>
            </View>
            <View style={styles.textInputContainer}>
                <TextField
                    id="toolbar-chat-input"
                    placeholder={t`Give me more detail`}
                    value={inputText}
                    onChange={(event) => {
                        updateSuggestion({
                            ...suggestion,
                            additionalText: event,
                        });
                    }}
                    disabled={false} // Enable typing in the TextField
                    autoFocus={true}
                    autoComplete="off"
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            event.preventDefault();
                            updateSuggestion(null);
                            handleSuggestionClick({
                                ...suggestion,
                                additionalText: inputText,
                            });
                        } else if (event.key === "Escape") {
                            event.preventDefault();
                            updateSuggestion(null);
                        }
                    }}
                />
                <IconButton
                    onClick={() => {
                        handleSuggestionClick({
                            ...suggestion,
                            additionalText: inputText,
                        });
                    }}
                    disabled={inputText.length === 0}
                    icon={paperAirplaneIcon}
                    kind="tertiary"
                    size="small"
                    style={styles.sendIconButton}
                />
            </View>
            {loading && (
                <View style={styles.loading}>
                    <LabelSmall>{t`Loading`}</LabelSmall>
                    <Strut size={spacing.xxxSmall_4} />
                    <LoadingDots color={color.offBlack64} size="small" />
                </View>
            )}
            {!loading && dynamicSuggestions.length && (
                <ActionBubbles
                    style={styles.bubbles}
                    onActionSelected={(bubble) => {
                        handleSuggestionClick({
                            ...suggestion,
                            additionalText: bubble.label,
                        });
                    }}
                    actionBubbles={dynamicSuggestions.map((s) => ({
                        label: s,
                        command: s,
                        actions: [],
                    }))}
                />
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    wrapper: {
        width: 400,
    },
    openerContainer: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        paddingInlineStart: spacing.xxxxSmall_2,
        paddingInlineEnd: spacing.small_12,
        paddingBlockEnd: spacing.xSmall_8,
        color: color.blue,
    },
    khanmigoIcon: {
        marginInlineStart: 0,
        marginInlineEnd: spacing.xSmall_8,
        width: spacing.xLarge_32,
        height: spacing.xLarge_32,
    },
    caretIcon: {
        paddingInlineStart: spacing.small_12,
    },
    editingTitle: {
        fontWeight: "normal",
        color: color.offBlack,
    },
    textInputContainer: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        padding: spacing.xSmall_8,
        paddingBlockStart: 0,
    },
    sendIconButton: {
        alignSelf: "center",
        margin: 0,
        marginInlineStart: spacing.xSmall_8,
    },
    loading: {
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        color: color.offBlack64,
        padding: spacing.xSmall_8,
    },
    bubbles: {
        gap: spacing.xSmall_8,
        padding: spacing.xSmall_8,
    },
});
